/**
 * Application Routes Configuration
 *
 * Defines all routes with authentication protection and layout structure.
 * Uses React Router v6 with nested routes and protected route components.
 */

import { Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { lazy, useEffect } from 'react';
import { ProtectedRoute, PublicRoute } from '@/components/layout/ProtectedRoute';
import { AdminGuard } from '@/components/layout/AdminGuard';
import { SuperAdminGuard } from '@/components/layout/SuperAdminGuard';
import { OrgOwnerOrSuperAdminGuard } from '@/components/layout/OrgOwnerOrSuperAdminGuard';
import { TaskQueueProvider } from '@/contexts/TaskQueueContext';
import { OrgContextProvider } from '@/contexts/OrgContext';
import { AppLayout } from '@/components/layout/AppLayout';
import { PortalLayout } from '@/components/portal/PortalLayout';
import { RouteSuspense } from '@/components/common/SuspenseWrapper';
import { useVideoDetail } from '@/hooks/api/useVideos';
import { usePortalHome } from '@/hooks/portal/usePortalHome';

// Static imports: auth pages and dashboard (critical path)
import { SignInPage } from '@/pages/auth/SignInPage';
import { CallbackPage } from '@/pages/auth/CallbackPage';
import { AcceptInvitePage } from '@/pages/auth/AcceptInvitePage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';

// Lazy-loaded page components
const AdminProfilePage = lazy(() => import('@/pages/profile/AdminProfilePage').then(m => ({ default: m.AdminProfilePage })));
const PortalProfilePage = lazy(() => import('@/pages/profile/PortalProfilePage').then(m => ({ default: m.PortalProfilePage })));
const AdminContentPage = lazy(() => import('@/pages/content/AdminContentPage').then(m => ({ default: m.AdminContentPage })));
const PortalContentPage = lazy(() => import('@/pages/content/PortalContentPage').then(m => ({ default: m.PortalContentPage })));
const AdminContentDetailPage = lazy(() => import('@/pages/content/AdminContentDetailPage').then(m => ({ default: m.AdminContentDetailPage })));
const PortalContentDetailPage = lazy(() => import('@/pages/content/PortalContentDetailPage').then(m => ({ default: m.PortalContentDetailPage })));
const ArticlesPage = lazy(() => import('@/pages/articles/ArticlesPage').then(m => ({ default: m.ArticlesPage })));
const ArticleDetailPage = lazy(() => import('@/pages/articles/ArticleDetailPage').then(m => ({ default: m.ArticleDetailPage })));
const VideosPage = lazy(() => import('@/pages/videos/VideosPage').then(m => ({ default: m.VideosPage })));
const AdminChannelIntelligencePage = lazy(() => import('@/pages/channels/AdminChannelIntelligencePage').then(m => ({ default: m.AdminChannelIntelligencePage })));
const PortalChannelIntelligencePage = lazy(() => import('@/pages/channels/PortalChannelIntelligencePage').then(m => ({ default: m.PortalChannelIntelligencePage })));
const PortalChannelComparisonPage = lazy(() => import('@/pages/channels/PortalChannelComparisonPage').then(m => ({ default: m.PortalChannelComparisonPage })));
const ChannelComparisonPage = lazy(() => import('@/pages/channels/ChannelComparisonPage'));
const AdminChannelRollupsPage = lazy(() =>
  import('@/pages/channels/AdminChannelRollupsPage').then((m) => ({
    default: m.AdminChannelRollupsPage,
  })),
);
const NewslettersPage = lazy(() => import('@/pages/newsletters/NewslettersPage').then(m => ({ default: m.NewslettersPage })));
const NewsletterDetailPage = lazy(() => import('@/pages/newsletters/NewsletterDetailPage').then(m => ({ default: m.NewsletterDetailPage })));
const NewsletterGeneratePage = lazy(() => import('@/pages/newsletters/NewsletterGeneratePage').then(m => ({ default: m.NewsletterGeneratePage })));
const AdminSourcesPage = lazy(() => import('@/pages/sources/AdminSourcesPage').then(m => ({ default: m.AdminSourcesPage })));
const CreatorsPage = lazy(() => import('@/pages/creators/CreatorsPage').then(m => ({ default: m.CreatorsPage })));
const CreatorDashboardPage = lazy(() => import('@/pages/creators/CreatorDashboardPage').then(m => ({ default: m.CreatorDashboardPage })));
const OutputsPage = lazy(() => import('@/pages/outputs/OutputsPage').then(m => ({ default: m.OutputsPage })));
const AssetDetailPage = lazy(() => import('@/pages/outputs/AssetDetailPage').then(m => ({ default: m.AssetDetailPage })));
const TemplatesLayout = lazy(() => import('@/pages/templates/TemplatesLayout').then(m => ({ default: m.TemplatesLayout })));
const TemplatesPage = lazy(() => import('@/pages/templates/TemplatesPage').then(m => ({ default: m.TemplatesPage })));
const TemplateEditPage = lazy(() => import('@/pages/templates/TemplateEditPage').then(m => ({ default: m.TemplateEditPage })));
const PersonaEditorPage = lazy(() => import('@/pages/templates/PersonaEditorPage').then(m => ({ default: m.PersonaEditorPage })));
const DomainPromptsPage = lazy(() => import('@/pages/domain-prompts/DomainPromptsPage').then(m => ({ default: m.DomainPromptsPage })));
const AdminPatternsPage = lazy(() => import('@/pages/patterns/AdminPatternsPage').then(m => ({ default: m.AdminPatternsPage })));
const PortalPatternsPage = lazy(() => import('@/pages/patterns/PortalPatternsPage').then(m => ({ default: m.PortalPatternsPage })));
const AdminPatternDetailPage = lazy(() => import('@/pages/patterns/AdminPatternDetailPage').then(m => ({ default: m.AdminPatternDetailPage })));
const PortalPatternDetailPage = lazy(() => import('@/pages/patterns/PortalPatternDetailPage').then(m => ({ default: m.PortalPatternDetailPage })));
const ProjectsPage = lazy(() => import('@/pages/projects/ProjectsPage').then(m => ({ default: m.ProjectsPage })));
const ProjectDetailPage = lazy(() => import('@/pages/projects/ProjectDetailPage').then(m => ({ default: m.ProjectDetailPage })));
const MorningCheckPage = lazy(() => import('@/pages/projects/MorningCheckPage').then(m => ({ default: m.MorningCheckPage })));
const RosterPage = lazy(() => import('@/pages/projects/RosterPage').then(m => ({ default: m.RosterPage })));
const SystemPage = lazy(() => import('@/pages/system/SystemPage').then(m => ({ default: m.SystemPage })));
const DesignSystemPage = lazy(() => import('@/pages/internal/DesignSystemPage').then(m => ({ default: m.DesignSystemPage })));
const CostDashboardPage = lazy(() => import('@/pages/costs/CostDashboardPage').then(m => ({ default: m.CostDashboardPage })));
const CostItemLedgerPage = lazy(() => import('@/pages/costs/CostItemLedgerPage').then(m => ({ default: m.CostItemLedgerPage })));
const QualityDashboardPage = lazy(() => import('@/pages/quality/QualityDashboardPage').then(m => ({ default: m.QualityDashboardPage })));
const McpUsagePage = lazy(() => import('@/pages/mcp/McpUsagePage').then(m => ({ default: m.McpUsagePage })));
const InboundEmailAdminPage = lazy(() => import('@/pages/admin/inbound-email/InboundEmailAdminPage').then(m => ({ default: m.InboundEmailAdminPage })));
const MCPSettingsPage = lazy(() => import('@/pages/settings/MCPSettingsPage').then(m => ({ default: m.MCPSettingsPage })));
const TokensPage = lazy(() => import('@/pages/settings/TokensPage').then(m => ({ default: m.TokensPage })));
// Sentinel moderator UI (Sprint 4a — gated by OrgOwnerOrSuperAdminGuard)
const AdminSentinelFlagsPage = lazy(() => import('@/pages/sentinel/AdminSentinelFlagsPage').then(m => ({ default: m.AdminSentinelFlagsPage })));
const SentinelCreatorPage = lazy(() => import('@/pages/sentinel/SentinelCreatorPage').then(m => ({ default: m.SentinelCreatorPage })));
const SentinelSettingsPage = lazy(() => import('@/pages/sentinel/SentinelSettingsPage').then(m => ({ default: m.SentinelSettingsPage })));

// Portal entities (CUR-1041 A)
const PortalEntitiesPage = lazy(() => import('@/pages/entities/PortalEntitiesPage').then(m => ({ default: m.PortalEntitiesPage })));
const PortalEntityDetailPage = lazy(() => import('@/pages/entities/PortalEntityDetailPage').then(m => ({ default: m.PortalEntityDetailPage })));

// Portal pages (lazy-loaded, separate chunk)
const PortalDashboardPage = lazy(() => import('@/pages/portal/PortalDashboardPage').then(m => ({ default: m.PortalDashboardPage })));
// Admin*/Portal* wrappers above (Content, ContentDetail, ChannelIntelligence, Patterns, PatternDetail)
// are Pattern C compositions around shared display components — see admin-dashboard/CLAUDE.md (CUR-533).
const PortalSourcesPage = lazy(() => import('@/pages/portal/PortalSourcesPage').then(m => ({ default: m.PortalSourcesPage })));
const PortalTeamPage = lazy(() => import('@/pages/portal/PortalTeamPage').then(m => ({ default: m.PortalTeamPage })));
const PortalUsagePage = lazy(() => import('@/pages/portal/PortalUsagePage').then(m => ({ default: m.PortalUsagePage })));
const PortalOnboardingPage = lazy(() => import('@/pages/portal/PortalOnboardingPage').then(m => ({ default: m.PortalOnboardingPage })));
const PortalProjectsPage = lazy(() => import('@/pages/portal/PortalProjectsPage').then(m => ({ default: m.PortalProjectsPage })));
const PortalProjectDetailPage = lazy(() => import('@/pages/portal/PortalProjectDetailPage').then(m => ({ default: m.PortalProjectDetailPage })));
const PortalSettingsPage = lazy(() => import('@/pages/portal/PortalSettingsPage').then(m => ({ default: m.PortalSettingsPage })));
const PortalBillingPage = lazy(() => import('@/pages/portal/PortalBillingPage').then(m => ({ default: m.PortalBillingPage })));
const PortalSentinelFlagsPage = lazy(() => import('@/pages/portal/PortalSentinelFlagsPage').then(m => ({ default: m.PortalSentinelFlagsPage })));
const PortalDigestsPage = lazy(() => import('@/pages/portal/PortalDigestsPage').then(m => ({ default: m.PortalDigestsPage })));

// Error boundary component
import { ErrorBoundary } from '@/components/common/ErrorBoundary';

/**
 * Redirects legacy /videos/:id (BIGINT) to unified /content/video/:slug
 */
function LegacyVideoRedirect() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const videoId = id ? Number.parseInt(id, 10) : 0;
  const { data, isError } = useVideoDetail(videoId, { enabled: !!videoId });

  useEffect(() => {
    if (data?.slug) {
      navigate(`/content/video/${data.slug}`, { replace: true });
    } else if (data?.content_id) {
      navigate(`/content/video/${data.content_id}`, { replace: true });
    } else if (isError) {
      navigate('/content', { replace: true });
    }
  }, [data, isError, navigate]);

  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

/**
 * Wraps `usePortalHome()` so the two `/portal` catch-all Navigate elements
 * (index route and path="*") can resolve the org's configured landing route
 * instead of hardcoding `/portal/dashboard`. A hook cannot be called inline
 * inside a `<Navigate to={...} />` prop expression, so it needs its own
 * component.
 */
function PortalHomeRedirect() {
  return <Navigate to={usePortalHome()} replace />;
}

export function AppRoutes() {
  return (
    <ErrorBoundary>
      <Routes>
        {/* Public routes (authentication) */}
        <Route
          path="/signin"
          element={
            <PublicRoute>
              <SignInPage />
            </PublicRoute>
          }
        />
        <Route
          path="/auth/sign-in"
          element={<Navigate to="/signin" replace />}
        />
        <Route
          path="/auth/callback"
          element={<CallbackPage />}
        />
        <Route
          path="/auth/accept-invite"
          element={<AcceptInvitePage />}
        />

        {/* Portal routes (must be before admin catch-all) */}
        <Route
          path="/portal/*"
          element={
            <ProtectedRoute>
              <OrgContextProvider>
                <PortalLayout />
              </OrgContextProvider>
            </ProtectedRoute>
          }
        >
          <Route index element={<PortalHomeRedirect />} />
          <Route path="dashboard" element={<RouteSuspense><PortalDashboardPage /></RouteSuspense>} />
          <Route path="content" element={<RouteSuspense><PortalContentPage /></RouteSuspense>} />
          <Route path="content/:contentId" element={<RouteSuspense><PortalContentDetailPage /></RouteSuspense>} />
          <Route path="patterns" element={<RouteSuspense><PortalPatternsPage /></RouteSuspense>} />
          <Route path="patterns/:patternId" element={<RouteSuspense><PortalPatternDetailPage /></RouteSuspense>} />
          <Route path="entities" element={<RouteSuspense><PortalEntitiesPage /></RouteSuspense>} />
          <Route path="entities/:entityId" element={<RouteSuspense><PortalEntityDetailPage /></RouteSuspense>} />
          <Route path="sentinel/flags" element={<RouteSuspense><PortalSentinelFlagsPage /></RouteSuspense>} />
          <Route path="sentinel/digests" element={<RouteSuspense><PortalDigestsPage /></RouteSuspense>} />
          <Route path="sources" element={<RouteSuspense><PortalSourcesPage /></RouteSuspense>} />
          <Route path="sources/compare" element={<RouteSuspense><PortalChannelComparisonPage /></RouteSuspense>} />
          <Route path="sources/:sourceId/intelligence" element={<RouteSuspense><PortalChannelIntelligencePage /></RouteSuspense>} />
          <Route path="sources/:sourceId/intelligence/:reportId" element={<RouteSuspense><PortalChannelIntelligencePage /></RouteSuspense>} />
          <Route path="team" element={<RouteSuspense><PortalTeamPage /></RouteSuspense>} />
          <Route path="usage" element={<RouteSuspense><PortalUsagePage /></RouteSuspense>} />
          <Route path="billing" element={<RouteSuspense><PortalBillingPage /></RouteSuspense>} />
          <Route path="profile" element={<RouteSuspense><PortalProfilePage /></RouteSuspense>} />
          <Route path="projects" element={<RouteSuspense><PortalProjectsPage /></RouteSuspense>} />
          <Route path="projects/:projectId" element={<RouteSuspense><PortalProjectDetailPage /></RouteSuspense>} />
          <Route path="creators/:creatorId" element={<RouteSuspense><CreatorDashboardPage /></RouteSuspense>} />
          <Route path="onboarding" element={<RouteSuspense><PortalOnboardingPage /></RouteSuspense>} />
          <Route path="settings" element={<RouteSuspense><PortalSettingsPage /></RouteSuspense>} />
          <Route path="settings/tokens" element={<RouteSuspense><TokensPage /></RouteSuspense>} />
          {/* MCP Access now renders inline in the Settings "MCP Access" tab (/portal/settings?tab=mcp). */}
          <Route path="*" element={<PortalHomeRedirect />} />
        </Route>

        {/* Protected routes (main application / admin) */}
        <Route
          path="/*"
          element={
            <TaskQueueProvider>
              <ProtectedRoute>
                <AdminGuard>
                  <OrgContextProvider>
                    <AppLayout />
                  </OrgContextProvider>
                </AdminGuard>
              </ProtectedRoute>
            </TaskQueueProvider>
          }
        >
          {/* Nested protected routes */}
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="content" element={<RouteSuspense><AdminContentPage /></RouteSuspense>} />
          <Route path="content/video/:contentId" element={<RouteSuspense><AdminContentDetailPage /></RouteSuspense>} />
          <Route path="content/article/:contentId" element={<RouteSuspense><AdminContentDetailPage /></RouteSuspense>} />
          <Route path="articles" element={<RouteSuspense><ArticlesPage /></RouteSuspense>} />
          <Route path="articles/:id" element={<RouteSuspense><ArticleDetailPage /></RouteSuspense>} />
          <Route path="videos" element={<RouteSuspense><VideosPage /></RouteSuspense>} />
          <Route path="videos/:id" element={<LegacyVideoRedirect />} />
          {/* Input Sources (multi-platform) — UI calls them "Sources" (CUR-528) */}
          <Route path="sources" element={<RouteSuspense><AdminSourcesPage /></RouteSuspense>} />
          {/* Legacy redirect: pre-CUR-528 bookmarks pointing at /inputs */}
          <Route path="inputs" element={<Navigate to="/sources" replace />} />
          {/* Cross-platform creator view */}
          <Route path="creators" element={<RouteSuspense><CreatorsPage /></RouteSuspense>} />
          {/* Sprint 4c UUID-keyed creator profile (parallel to legacy /creators) */}
          <Route path="creators/:creatorId" element={<RouteSuspense><CreatorDashboardPage /></RouteSuspense>} />

          {/* Channel Intelligence hub — YouTube channel management, comparison, analytics */}
          <Route path="channels/compare" element={<RouteSuspense><ChannelComparisonPage /></RouteSuspense>} />
          <Route
            path="channels/rollups"
            element={
              <RouteSuspense>
                <SuperAdminGuard>
                  <AdminChannelRollupsPage />
                </SuperAdminGuard>
              </RouteSuspense>
            }
          />
          <Route path="channels/:publisherId/intelligence" element={<RouteSuspense><AdminChannelIntelligencePage /></RouteSuspense>} />
          <Route path="channels" element={<Navigate to="/sources" replace />} />

          <Route path="patterns" element={<SuperAdminGuard><RouteSuspense><AdminPatternsPage /></RouteSuspense></SuperAdminGuard>} />
          <Route path="patterns/:patternId" element={<SuperAdminGuard><RouteSuspense><AdminPatternDetailPage /></RouteSuspense></SuperAdminGuard>} />

          {/* Admin Projects (cross-org, super-admin only — CUR-530, Pattern B) */}
          <Route path="projects" element={<SuperAdminGuard><RouteSuspense><ProjectsPage /></RouteSuspense></SuperAdminGuard>} />
          <Route path="projects/:projectId" element={<SuperAdminGuard><RouteSuspense><ProjectDetailPage /></RouteSuspense></SuperAdminGuard>} />
          {/* Sprint 4c #2 — Journey-A Roster (super-admin only, Pattern B) */}
          <Route path="projects/:projectId/roster" element={<SuperAdminGuard><RouteSuspense><RosterPage /></RouteSuspense></SuperAdminGuard>} />
          {/* Sprint 4c #7 — Journey-C Morning Check landing (super-admin only, Pattern B) */}
          <Route path="projects/:projectId/morning-check" element={<SuperAdminGuard><RouteSuspense><MorningCheckPage /></RouteSuspense></SuperAdminGuard>} />


          {/* Output Channels (multi-platform publishing) */}
          <Route path="outputs" element={<RouteSuspense><OutputsPage /></RouteSuspense>} />
          <Route path="outputs/assets/:assetId" element={<RouteSuspense><AssetDetailPage /></RouteSuspense>} />

          {/* Newsletters list (all statuses including failed) */}
          <Route path="newsletters" element={<RouteSuspense><NewslettersPage /></RouteSuspense>} />
          <Route path="newsletters/:id" element={<RouteSuspense><NewsletterDetailPage /></RouteSuspense>} />
          <Route path="newsletters/generate" element={<RouteSuspense><NewsletterGeneratePage /></RouteSuspense>} />

          {/* Templates with nested routes */}
          <Route path="templates" element={<RouteSuspense><TemplatesLayout /></RouteSuspense>}>
            <Route index element={<RouteSuspense><TemplatesPage /></RouteSuspense>} />
            <Route path="domain-prompts" element={<SuperAdminGuard><RouteSuspense><DomainPromptsPage /></RouteSuspense></SuperAdminGuard>} />
          </Route>
          <Route path="templates/:id/edit" element={<RouteSuspense><TemplateEditPage /></RouteSuspense>} />
          <Route path="templates/:id/persona" element={<RouteSuspense><PersonaEditorPage /></RouteSuspense>} />

          <Route path="system" element={<RouteSuspense><SystemPage /></RouteSuspense>} />
          {/* Sprint 4 Prep — internal design-system preview (super-admin only) */}
          <Route path="internal/design-system" element={<SuperAdminGuard><RouteSuspense><DesignSystemPage /></RouteSuspense></SuperAdminGuard>} />
          <Route path="costs" element={<SuperAdminGuard><RouteSuspense><CostDashboardPage /></RouteSuspense></SuperAdminGuard>} />
          <Route path="costs/items/:contentId" element={<SuperAdminGuard><RouteSuspense><CostItemLedgerPage /></RouteSuspense></SuperAdminGuard>} />
          <Route path="quality" element={<SuperAdminGuard><RouteSuspense><QualityDashboardPage /></RouteSuspense></SuperAdminGuard>} />
          <Route path="inbound-email" element={<SuperAdminGuard><RouteSuspense><InboundEmailAdminPage /></RouteSuspense></SuperAdminGuard>} />

          {/* Sentinel moderator UI (Sprint 4a) — Org-Owner/Admin or super_admin */}
          <Route
            path="sentinel/flags"
            element={
              <OrgOwnerOrSuperAdminGuard>
                <RouteSuspense><AdminSentinelFlagsPage /></RouteSuspense>
              </OrgOwnerOrSuperAdminGuard>
            }
          />
          <Route
            path="sentinel/creators/:handle"
            element={
              <OrgOwnerOrSuperAdminGuard>
                <RouteSuspense><SentinelCreatorPage /></RouteSuspense>
              </OrgOwnerOrSuperAdminGuard>
            }
          />
          <Route
            path="sentinel/settings"
            element={
              <OrgOwnerOrSuperAdminGuard>
                <RouteSuspense><SentinelSettingsPage /></RouteSuspense>
              </OrgOwnerOrSuperAdminGuard>
            }
          />
          <Route path="sentinel" element={<Navigate to="/sentinel/flags" replace />} />
          <Route path="mcp-usage" element={<SuperAdminGuard><RouteSuspense><McpUsagePage /></RouteSuspense></SuperAdminGuard>} />
          <Route path="profile" element={<RouteSuspense><AdminProfilePage /></RouteSuspense>} />
          <Route path="settings/mcp" element={<RouteSuspense><MCPSettingsPage /></RouteSuspense>} />
          <Route path="settings/tokens" element={<RouteSuspense><TokensPage /></RouteSuspense>} />
          <Route path="domain-prompts" element={<SuperAdminGuard><RouteSuspense><DomainPromptsPage /></RouteSuspense></SuperAdminGuard>} />

          {/* Catch-all redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>

        {/* Root redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}
